// SwipeBottom.js
import React, { useState, useEffect, useRef } from "react";
import { Box, Fade, Slide, styled } from "@mui/material";
import Backdrop from "./Backdrop";
import { grey } from "@mui/material/colors";

const StyledBox = styled("div")(({ theme }) => ({
  backgroundColor: theme.palette.mode === "light" ? "#fff" : grey[800],
}));

const Puller = styled("div")(({ theme }) => ({
  width: 30,
  height: 6,
  backgroundColor: theme.palette.mode === "light" ? grey[300] : grey[900],
  borderRadius: 3,
  position: "absolute",
  top: 8,
  left: "calc(50% - 15px)",
}));

const SwipeBottom = ({ summaryClosed, children }) => {
  const expandedHeight = 50; // 50% de la altura de la ventana
  const collapsedHeight = 145; // Altura cuando está cerrado
  const [isExpanded, setIsExpanded] = useState(false); // Controla si el SwipeBottom está expandido o no
  const [isDragging, setIsDragging] = useState(false);
  const [startY, setStartY] = useState(0);
  const [translateY, setTranslateY] = useState(`calc(${expandedHeight}vh - ${collapsedHeight})`);
  const containerRef = useRef(null); // Referencia al contenedor desplazable
  const scrollableRef = useRef(null); // Referencia al contenedor desplazable

  const timeoutTransition = 300;

  // Función para manejar el inicio del deslizamiento
  const handleStart = (clientY) => {
    const scrollTop = scrollableRef.current.scrollTop;

    if (scrollTop === 0 || !isExpanded) {
      // Solo permitir si el scroll está en 0
      setIsDragging(true);
      setStartY(clientY);
      document.body.style.overflow = "hidden"; // Bloquear el scroll del body durante el swipe
    }
  };

  // Función para manejar el movimiento del deslizamiento
  const handleMove = (clientY) => {
    if (isDragging) {
      const diffY = startY - clientY;
      if (diffY > 50) {
        setIsExpanded(true); // Expande el BottomSheet
      } else if (diffY < -50) {
        setIsExpanded(false); // Contrae el BottomSheet
      }
    }
  };

  // Función para finalizar el deslizamiento
  const handleEnd = () => {
    setIsDragging(false);
    // Restaurar el scroll del body cuando termina el swipe
    document.body.style.overflow = "auto";
  };

  // Listeners para eventos de ratón y táctiles
  useEffect(() => {
    const handleMouseMove = (event) => handleMove(event.clientY);
    const handleMouseUp = () => handleEnd();
    const handleTouchMove = (event) => handleMove(event.touches[0]?.clientY);
    const handleTouchEnd = () => handleEnd();

    if (isDragging) {
      window.addEventListener("mousemove", handleMouseMove);
      window.addEventListener("mouseup", handleMouseUp);
      window.addEventListener("touchmove", handleTouchMove);
      window.addEventListener("touchend", handleTouchEnd);
    }

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
      window.removeEventListener("touchmove", handleTouchMove);
      window.removeEventListener("touchend", handleTouchEnd);
    };
  }, [isDragging]);

  useEffect(() => {
    if (isExpanded) {
      setTranslateY("0px");
      document.querySelector(".cart-footer .footer-bottom").style.paddingBottom = `calc(${expandedHeight}vh + 10px)`;
    } else {
      setTranslateY(`calc(${expandedHeight}vh - ${collapsedHeight}px)`);
      document.querySelector(".cart-footer .footer-bottom").style.paddingBottom = `${collapsedHeight + 10}px`;
    }

    return () => {
      // regresa el margin bottom al footer a 0, cuando se desmonta el componente
      document.querySelector(".cart-footer .footer-bottom").style.paddingBottom = null;
    };
  }, [isExpanded]);

  return (
    <>
      {isExpanded && (
        <Backdrop
          onClick={() => {
            setIsExpanded(false);
          }}
        />
      )}

      <Box
        ref={containerRef}
        className="swipe-bottom-content"
        sx={{
          position: "fixed",
          bottom: 0,
          left: 0,
          width: "100%",
          zIndex: 1000,
          transitionProperty: "transform",
          transitionDuration: `${timeoutTransition}ms`,
          transitionTimingFunction: "ease",
          height: isExpanded ? "100vh" : `${collapsedHeight}px`,
          background: "white",
          boxShadow: 3,
          borderTopLeftRadius: 8,
          borderTopRightRadius: 8,
          p: 2,
          pt: "20px",
          transform: isExpanded ? "translateY(0)" : `translateY(calc(100% - ${collapsedHeight}px))`, // Movimiento dinámico durante el arrastre
        }}
        // onMouseDown={(e) => handleStart(e.clientY)} // acepta el evento de deslizar sin touch
        onTouchStart={(e) => handleStart(e.touches[0]?.clientY)} // evento deslizar con touch
      >
        <Puller
          sx={{
            width: '60px',
            left: 'calc(50% - 30px)'
          }}
          onClick={() => {
            setIsExpanded(!isExpanded);
          }}
        />

        {!isExpanded && (
          <Fade in={!isExpanded} timeout={timeoutTransition}>
            <StyledBox className="content-swipeable-drawer" sx={{ p: 1 }}>
              {summaryClosed}
            </StyledBox>
          </Fade>
        )}

        <Slide direction="up" in={isExpanded} timeout={timeoutTransition}>
          <StyledBox
            ref={scrollableRef}
            sx={{
              px: 2,
              pb: 2,
              height: "100%",
              overflow: "auto",
              backgroundColor: "#F3F4F5",
            }}
          >
            {children}
          </StyledBox>
        </Slide>
      </Box>
    </>
  );
};

export default SwipeBottom;
